import React, { useState, useMemo } from 'react';
import { Copy, Check } from 'lucide-react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { AttachmentRenderer } from '../attachments/AttachmentRenderer';

interface MessageRendererProps {
  content: string;
  /** Optional UUID→displayName map to resolve <@uuid> mention tokens */
  membersMap?: Record<string, string>;
}

/** Check if the text consists exclusively of emojis and whitespace */
export const isEmojiOnly = (text: string): boolean => {
  if (!text) return false;
  const stripped = text.replace(/[\s\n\r]/g, '');
  if (!stripped) return false;
  
  // If it contains any alphanumeric characters or standard ASCII punctuation, it is NOT emoji-only
  if (/[a-zA-Z0-9.,?!;:'"()\[\]{}\/\\@#$%^&*_=+|~<>-]/.test(stripped)) {
    return false;
  }
  
  // Matches unicode emojis including skin tones, zero-width joiners, and variation selectors
  const emojiRegex = /^(\p{Extended_Pictographic}|\p{Emoji_Presentation}|\u200d|\uFE0F)+$/u;
  return emojiRegex.test(stripped);
};

/** Normalize and validate safe URLs (prevents javascript: and dangerous URI schemes) */
const getSafeUrl = (rawUrl: string): string | null => {
  try {
    const trimmed = rawUrl.trim();
    if (!trimmed) return null;

    if (trimmed.startsWith('/api/')) {
      return trimmed;
    }
    
    // Explicitly allow http, https, mailto, tel
    if (/^(https?:\/\/|mailto:|tel:)/i.test(trimmed)) {
      const parsed = new URL(trimmed);
      if (['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol.toLowerCase())) {
        return trimmed;
      }
    } else if (/^www\./i.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return null;
  } catch {
    return null;
  }
};

/** Format human-readable language label */
const formatLanguageLabel = (lang?: string): string => {
  if (!lang) return 'Plain Text';
  const clean = lang.trim().toLowerCase();
  const map: Record<string, string> = {
    js: 'JavaScript',
    javascript: 'JavaScript',
    ts: 'TypeScript',
    typescript: 'TypeScript',
    jsx: 'React JSX',
    tsx: 'React TSX',
    java: 'Java',
    py: 'Python',
    python: 'Python',
    json: 'JSON',
    html: 'HTML',
    css: 'CSS',
    sql: 'SQL',
    bash: 'Bash',
    sh: 'Shell',
    shell: 'Shell',
    c: 'C',
    cpp: 'C++',
    'c++': 'C++',
    cs: 'C#',
    csharp: 'C#',
    go: 'Go',
    golang: 'Go',
    rust: 'Rust',
    rs: 'Rust',
    php: 'PHP',
    ruby: 'Ruby',
    rb: 'Ruby',
    xml: 'XML',
    yaml: 'YAML',
    yml: 'YAML',
    md: 'Markdown',
    markdown: 'Markdown',
    dockerfile: 'Dockerfile',
  };
  return map[clean] || (clean.charAt(0).toUpperCase() + clean.slice(1));
};

/** Dark themed code block panel with copy button and language badge */
const CodeBlock: React.FC<{ code: string; language?: string | undefined }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback if clipboard API fails
    }
  };

  const formattedLang = formatLanguageLabel(language);

  return (
    <div className="relative my-2 rounded-2xl overflow-hidden bg-[#0A0D14] border border-[#1E2640] shadow-xl text-left font-sans">
      {/* Code header bar: language name on left, copy button on right */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#0F1424] border-b border-[#1E2640] select-none">
        <span className="text-xs font-semibold tracking-wide text-slate-400">
          {formattedLang}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-slate-300 hover:text-white bg-slate-800/80 hover:bg-slate-700 transition"
          title="Copy code"
          aria-label="Copy code to clipboard"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-emerald-400" />
              <span className="text-emerald-400">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5 text-slate-400" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      {/* Code body: Preserves exact whitespace, tabs, line breaks, and allows horizontal scroll */}
      <pre className="p-4 text-xs sm:text-sm font-mono text-[#E2E8F0] overflow-x-auto whitespace-pre leading-relaxed select-text no-scrollbar">
        <code>{code}</code>
      </pre>
    </div>
  );
};

/** Parse inline formatting (bold, italic, strikethrough, inline code, links, mentions) safely */
const parseInlineTokens = (text: string, membersMap?: Record<string, string>): React.ReactNode[] => {
  if (!text) return [];

  // Match:
  // 1. Inline code: `code`
  // 2. Bold: **bold** or __bold__
  // 3. Italic: *italic* or _italic_
  // 4. Strikethrough: ~~strike~~
  // 5. Markdown Link: [text](url)
  // 6. Auto-URL: https://... or http://...
  // 7. Structured Mention: <@UUID> or <@username>
  // 8. Plain Mention: @username
  const inlineRegex = /(`[^`\n]+`)|(\*\*[^*]+\*\*|__[^_]+__)|(\*[^*\n]+\*|_[^_\n]+_)|(~~[^~\n]+~~)|(\[[^\]]+\]\([^\s)]+\))|(https?:\/\/[^\s<]+[^<.,:;"')\]\s])|(<@[0-9a-fA-F-]{36}>|<@[a-zA-Z0-9_.-]+>)|(@[a-zA-Z0-9_.-]+)/g;

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = inlineRegex.exec(text)) !== null) {
    // Append preceding plain text
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const fullMatch = match[0];
    const key = `${match.index}-${fullMatch.slice(0, 8)}`;

    // 1. Inline Code
    if (fullMatch.startsWith('`') && fullMatch.endsWith('`') && fullMatch.length > 2) {
      const codeContent = fullMatch.slice(1, -1);
      nodes.push(
        <code
          key={key}
          className="px-1.5 py-0.5 mx-0.5 rounded text-xs font-mono bg-muted text-foreground border border-border inline-block align-baseline select-text"
        >
          {codeContent}
        </code>
      );
    }
    // 2. Bold (**text** or __text__)
    else if ((fullMatch.startsWith('**') && fullMatch.endsWith('**')) || (fullMatch.startsWith('__') && fullMatch.endsWith('__'))) {
      const inner = fullMatch.slice(2, -2);
      nodes.push(
        <strong key={key} className="font-bold text-foreground">
          {parseInlineTokens(inner, membersMap)}
        </strong>
      );
    }
    // 3. Strikethrough (~~text~~)
    else if (fullMatch.startsWith('~~') && fullMatch.endsWith('~~')) {
      const inner = fullMatch.slice(2, -2);
      nodes.push(
        <del key={key} className="line-through text-muted-foreground">
          {parseInlineTokens(inner, membersMap)}
        </del>
      );
    }
    // 4. Italic (*text* or _text_)
    else if ((fullMatch.startsWith('*') && fullMatch.endsWith('*')) || (fullMatch.startsWith('_') && fullMatch.endsWith('_'))) {
      const inner = fullMatch.slice(1, -1);
      nodes.push(
        <em key={key} className="italic">
          {parseInlineTokens(inner, membersMap)}
        </em>
      );
    }
    // 5. Markdown Link: [text](url)
    else if (fullMatch.startsWith('[') && fullMatch.includes('](')) {
      const linkMatch = fullMatch.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch && linkMatch[1] && linkMatch[2]) {
        const linkText = linkMatch[1];
        const rawHref = linkMatch[2];
        const safeHref = getSafeUrl(rawHref);

        if (safeHref) {
          if (linkText.startsWith('📎')) {
            const rawText = linkText.replace(/^📎\s*/, '');
            let fileName = rawText;
            let fileSize: number | undefined = undefined;

            // Extract filename and size if in format "filename (size)"
            const sizeMatch = rawText.match(/^(.*)\s+\(([^)]+)\)$/);
            if (sizeMatch && sizeMatch[1]) {
              fileName = sizeMatch[1].trim();
              // Parse size string back to bytes for display
              const sizeStr = sizeMatch[2] || '';
              const sizeNum = parseFloat(sizeStr);
              if (!isNaN(sizeNum)) {
                if (sizeStr.includes('GB')) fileSize = Math.round(sizeNum * 1024 * 1024 * 1024);
                else if (sizeStr.includes('MB')) fileSize = Math.round(sizeNum * 1024 * 1024);
                else if (sizeStr.includes('KB')) fileSize = Math.round(sizeNum * 1024);
                else fileSize = Math.round(sizeNum);
              }
            }

            // Extract workspaceId and fileId from URL for presigned URL resolution
            // URL format: /api/v1/workspaces/{wid}/files/{fid}/download
            const fileIdMatch = safeHref.match(/\/workspaces\/([0-9a-fA-F-]{36})\/files\/([0-9a-fA-F-]{36})/);
            const workspaceId = fileIdMatch?.[1];
            const fileId = fileIdMatch?.[2];

            nodes.push(
              <div key={key} className="my-1.5 max-w-full">
                <AttachmentRenderer
                  fileName={fileName}
                  url={safeHref}
                  fileSize={fileSize}
                  workspaceId={workspaceId}
                  fileId={fileId}
                />
              </div>
            );
          } else {
            nodes.push(
              <a
                key={key}
                href={safeHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-medium underline underline-offset-2 hover:opacity-80 transition break-all inline"
                onClick={(e) => e.stopPropagation()}
              >
                {linkText}
              </a>
            );
          }
        } else {
          nodes.push(fullMatch);
        }
      } else {
        nodes.push(fullMatch);
      }
    }
    // 6. Auto-URL
    else if (/^https?:\/\//i.test(fullMatch)) {
      const safeHref = getSafeUrl(fullMatch);
      if (safeHref) {
        nodes.push(
          <a
            key={key}
            href={safeHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary font-medium underline underline-offset-2 hover:opacity-80 transition break-all inline"
            onClick={(e) => e.stopPropagation()}
          >
            {fullMatch}
          </a>
        );
      } else {
        nodes.push(fullMatch);
      }
    }
    // 7. Structured Mention (<@UUID> or <@username>)
    else if (fullMatch.startsWith('<@') && fullMatch.endsWith('>')) {
      const targetIdOrName = fullMatch.slice(2, -1).trim();
      const isUuid = /^[0-9a-fA-F-]{36}$/.test(targetIdOrName);
      const displayName = membersMap?.[targetIdOrName] || (isUuid ? 'member' : targetIdOrName);
      nodes.push(
        <span
          key={key}
          className="font-semibold px-1.5 py-0.5 rounded-lg text-xs mx-0.5 bg-primary/15 text-primary border border-primary/20 inline-flex items-center gap-0.5 shadow-xs select-text"
          title={isUuid ? `User ID: ${targetIdOrName}` : undefined}
        >
          <span>@{displayName}</span>
        </span>
      );
    }
    // 8. Plain Mention (@username)
    else if (fullMatch.startsWith('@')) {
      nodes.push(
        <span
          key={key}
          className="font-semibold px-1.5 py-0.5 rounded-lg text-xs mx-0.5 bg-primary/15 text-primary border border-primary/20 inline-flex items-center gap-0.5 shadow-xs select-text"
        >
          <span>{fullMatch}</span>
        </span>
      );
    } else {
      nodes.push(fullMatch);
    }

    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
};

/** Render block-level elements (code blocks, headings, blockquotes, paragraphs with line breaks) */
const renderBlocks = (rawContent: string, membersMap?: Record<string, string>): React.ReactNode[] => {
  if (!rawContent) return [];

  const blocks: React.ReactNode[] = [];
  
  // Split rawContent into fenced code blocks and regular text
  // Matches ```[language]\n[code]```
  const codeBlockRegex = /```([a-zA-Z0-9_+#.-]*)\r?\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = codeBlockRegex.exec(rawContent)) !== null) {
    // Process text before the code block
    if (match.index > lastIndex) {
      const textChunk = rawContent.slice(lastIndex, match.index);
      blocks.push(...renderTextBlocks(textChunk, `text-${lastIndex}`, membersMap));
    }

    const language = match[1]?.trim() || undefined;
    const code = match[2] || '';
    blocks.push(
      <CodeBlock
        key={`code-block-${match.index}`}
        code={code.replace(/\r?\n$/, '')} // Strip trailing newline if any
        language={language}
      />
    );

    lastIndex = match.index + match[0].length;
  }

  // Process remaining text after the last code block
  if (lastIndex < rawContent.length) {
    const textChunk = rawContent.slice(lastIndex);
    blocks.push(...renderTextBlocks(textChunk, `text-${lastIndex}`, membersMap));
  }

  return blocks;
};

/** Process regular text chunks into headings, blockquotes, and paragraphs */
const renderTextBlocks = (chunk: string, prefixKey: string, membersMap?: Record<string, string>): React.ReactNode[] => {
  if (!chunk) return [];

  const lines = chunk.split(/\r?\n/);
  const elements: React.ReactNode[] = [];
  let currentQuoteLines: string[] = [];
  let currentParagraphLines: string[] = [];

  const flushQuote = (index: number) => {
    if (currentQuoteLines.length > 0) {
      const quoteText = currentQuoteLines.join('\n');
      elements.push(
        <blockquote
          key={`${prefixKey}-quote-${index}`}
          className="border-l-4 border-primary/60 bg-muted/30 px-3.5 py-1.5 my-1.5 text-muted-foreground rounded-r-xl italic leading-relaxed select-text"
        >
          {quoteText.split('\n').map((line, lIdx) => (
            <React.Fragment key={lIdx}>
              {lIdx > 0 && <br />}
              {parseInlineTokens(line, membersMap)}
            </React.Fragment>
          ))}
        </blockquote>
      );
      currentQuoteLines = [];
    }
  };

  const flushParagraph = (index: number) => {
    if (currentParagraphLines.length > 0) {
      const paragraphContent: React.ReactNode[] = [];
      currentParagraphLines.forEach((pLine, pIdx) => {
        if (pIdx > 0) {
          paragraphContent.push(<br key={`br-${index}-${pIdx}`} />);
        }
        paragraphContent.push(...parseInlineTokens(pLine, membersMap));
      });

      elements.push(
        <p key={`${prefixKey}-p-${index}`} className="my-0.5 leading-relaxed break-words select-text">
          {paragraphContent}
        </p>
      );
      currentParagraphLines = [];
    }
  };

  lines.forEach((line, idx) => {
    // Blockquote line starting with >
    if (line.startsWith('> ') || line === '>') {
      flushParagraph(idx);
      currentQuoteLines.push(line.replace(/^>\s?/, ''));
    } else if (/^#{1,3}\s+/.test(line)) {
      flushParagraph(idx);
      flushQuote(idx);
      const headingMatch = line.match(/^(#{1,3})\s+(.*)$/);
      if (headingMatch && headingMatch[1] && headingMatch[2]) {
        const level = headingMatch[1].length;
        const headingText = headingMatch[2];
        if (level === 1) {
          elements.push(
            <h1 key={`${prefixKey}-h1-${idx}`} className="text-xl sm:text-2xl font-bold text-foreground my-1.5 leading-snug">
              {parseInlineTokens(headingText, membersMap)}
            </h1>
          );
        } else if (level === 2) {
          elements.push(
            <h2 key={`${prefixKey}-h2-${idx}`} className="text-lg sm:text-xl font-bold text-foreground my-1 leading-snug">
              {parseInlineTokens(headingText, membersMap)}
            </h2>
          );
        } else {
          elements.push(
            <h3 key={`${prefixKey}-h3-${idx}`} className="text-base sm:text-lg font-semibold text-foreground my-0.5 leading-snug">
              {parseInlineTokens(headingText, membersMap)}
            </h3>
          );
        }
      }
    } else {
      flushQuote(idx);
      currentParagraphLines.push(line);
    }
  });

  flushParagraph(lines.length);
  flushQuote(lines.length);

  return elements;
};

/**
 * MessageRenderer component
 * Safely renders rich text, code blocks, inline code, quotes, mentions, and links
 * identically for both sender and receiver.
 *
 * membersMap: optional Record<userId, displayName> used to resolve <@uuid> mention tokens.
 */
export const MessageRenderer: React.FC<MessageRendererProps> = ({ content, membersMap: propMembersMap }) => {
  if (!content) return null;

  const workspaceContext = useWorkspace();
  const members = workspaceContext?.members || [];

  const resolvedMembersMap = useMemo(() => {
    const map: Record<string, string> = { ...propMembersMap };
    members.forEach((m) => {
      if (m?.user?.id && m?.user?.displayName) {
        map[m.user.id] = m.user.displayName;
      }
    });
    return map;
  }, [members, propMembersMap]);

  // Render enlarged emojis only if content consists exclusively of emojis (no letters/numbers/punctuation)
  if (isEmojiOnly(content)) {
    return (
      <div className="text-3xl sm:text-4xl leading-snug py-1 select-text">
        {content}
      </div>
    );
  }

  try {
    return (
      <div className="text-sm text-foreground leading-relaxed break-words max-w-full space-y-1">
        {renderBlocks(content, resolvedMembersMap)}
      </div>
    );
  } catch (err) {
    // Graceful fallback on unexpected parsing errors
    console.error('[MessageRenderer] Graceful fallback on parse error', err);
    return (
      <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words">
        {content}
      </div>
    );
  }
};
